import React, { useState, useEffect, useImperativeHandle, useRef, useMemo, useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CollapseButton } from '../ui/CollapseButton';
import { SectionLinkContextMenu, type SectionLinkContextMenuAnchor } from './SectionLinkContextMenu';
import { SkillData } from '@skill-trees/shared';
import { TreeInteractor } from '../tree-domain/TreeInteractor';
import { SkillPanelInteractor, SkillPanelState } from './SkillPanelInteractor';
import { buildTreeViewUrl } from '../routing/routing';
import { resolveDescriptionClickAction } from './descriptionClickAction';
import { useSkillPanelFragmentScroll } from './useSkillPanelFragmentScroll';
import {
    buildSkillPanelFragmentScrollReadiness,
    tryScrollSkillPanelToFragment,
} from './skillPanelScroll';
import { resolveSectionLinkHeadingFromClickTarget } from './sectionLinkContextMenuPlacement';
import { isEventOutsideElement } from '../ui/outsideClick';
import { SKILL_PANEL_COLLAPSED_CHROME_WIDTH_PX } from './skillPanelViewport';
import { useIsEditLayoutNarrowViewport } from '../ui/useIsNarrowViewport';
import { undoRedoModifierLabel } from '../tree-domain/useUndoRedoShortcuts';
import { TreeInteractorContext } from '../tree-domain/treeInteractorContextInstance';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import type { GuideEditorOptions } from './GuideEditor';

/**
 * SimpleMDE lives behind this boundary so a read-only viewer never loads the editor
 * or its stylesheet. It is only reachable in development mode, while editing.
 */
const GuideEditor = React.lazy(() => import('./GuideEditor'));

const MENU_OUTSIDE_LISTEN_DELAY_MS = 0;

const SKILL_PANEL_DEFAULT_WIDTH_PX = 400;

const DRAFT_AUTOSAVE_DEBOUNCE_MS = 3000;

/** Wait after narrow first-land (collapsed) before playing the bounce hint (ms). */
const NARROW_COLLAPSED_BOUNCE_DELAY_MS = 6000;

/** Dev overflow menu: show “Add guide” when the leaf has no linked guide and the panel is idle (not editing or loading). */
function shouldShowDevAddGuideMenuItem(
    isDevelopment: boolean | undefined,
    panelState: SkillPanelState,
    skillTreeData: SkillData,
    skillId: string | null,
): boolean {
    return (
        isDevelopment === true &&
        !panelState.isEditing &&
        panelState.skill != null &&
        !panelState.loading &&
        !SkillPanelInteractor.leafHasLinkedGuide(skillTreeData, skillId)
    );
}

export interface SkillPanelHandle {
    /** Scroll the guide to a heading id (animation-script step). No-op when the panel cannot scroll (collapsed, loading, editing). */
    scrollToSection(sectionId: string): void;
}

interface SkillPanelProps {
    skillId: string | null;
    skillTreeData: SkillData;
    onCollapse: () => void;
    isCollapsed: boolean;
    interactor: TreeInteractor;
    onNodeSelect?: (nodeId: string) => void;
    isDevelopment?: boolean;
    width?: number;
    narrowCollapsedBounceToken?: number;
    onNarrowCollapsedBounceHandled?: () => void;
    /**
     * Base origin for shareable links. Omit to use the current page's origin (the hosted
     * app); pass `null` for a deployed artifact, which has no public home — that hides
     * Copy Link, the section context menu and the per-heading copy buttons.
     */
    publicUrl?: string | null;
    /**
     * False when the viewer is embedded in someone else's page: following a guide's
     * section link must not reset the host document's scroll position.
     */
    ownsDocumentScroll?: boolean;
    ref?: React.Ref<SkillPanelHandle>;
}

export const SkillPanel: React.FC<SkillPanelProps> = ({
    skillId,
    skillTreeData,
    onCollapse,
    isCollapsed,
    interactor,
    onNodeSelect,
    isDevelopment = false,
    width = SKILL_PANEL_DEFAULT_WIDTH_PX,
    narrowCollapsedBounceToken = 0,
    onNarrowCollapsedBounceHandled,
    publicUrl,
    ownsDocumentScroll = true,
    ref,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isNarrow = useIsEditLayoutNarrowViewport();
    // Create interactor instance - only recreate when interactor changes, not skillTreeData
    const panelInteractor = useMemo(() => {
        return new SkillPanelInteractor(
            interactor,
            skillTreeData,
            publicUrl === undefined ? window.location.origin : publicUrl,
        );
    }, [interactor, publicUrl]);
    const canShareLinks = panelInteractor.canShareLinks;
    const useTextareaEditor = panelInteractor.shouldUseTextareaEditor(isNarrow);

    // State from interactor
    const [panelState, setPanelState] = useState<SkillPanelState>(panelInteractor.getState());
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [sectionLinkMenu, setSectionLinkMenu] = useState<SectionLinkContextMenuAnchor | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const prevDocumentsRef = useRef<string>('');
    const prevSkillIdRef = useRef<string | null>(null);
    const panelContentRef = useRef<HTMLDivElement>(null);
    const [narrowBounceChrome, setNarrowBounceChrome] = useState(false);
    const [showDeleteGuideModal, setShowDeleteGuideModal] = useState(false);

    // Subscribe to interactor state changes
    useEffect(() => {
        panelInteractor.setUpdateCallback((state: SkillPanelState) => {
            setPanelState(state);
        });
        // Initialize with current state
        setPanelState(panelInteractor.getState());
    }, [panelInteractor]);

    // Keep the interactor's tree data in sync during render, not in an effect: the
    // title and comes-from/leads-to lists are computed while rendering, so an
    // effect-based sync left them one data change behind (e.g. after a rename)
    // with nothing triggering a repaint. updateSkillTreeData is a plain setter,
    // so syncing here is idempotent and safe.
    panelInteractor.updateSkillTreeData(skillTreeData);

    // Handle click outside menu
    useEffect(() => {
        if (!isMenuOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (isEventOutsideElement(event, menuRef.current)) {
                setIsMenuOpen(false);
            }
        };

        // Add a small delay to prevent immediate closure when opening
        const timeoutId = setTimeout(() => {
            // Listen for various event types to catch all interactions
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('click', handleClickOutside);
            document.addEventListener('pointerup', handleClickOutside);
            
            // Also listen specifically on SVG elements (skill tree canvas)
            const svgElements = document.querySelectorAll('svg');
            svgElements.forEach(svg => {
                svg.addEventListener('mousedown', handleClickOutside);
                svg.addEventListener('click', handleClickOutside);
                svg.addEventListener('pointerup', handleClickOutside);
            });
        }, MENU_OUTSIDE_LISTEN_DELAY_MS);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('pointerup', handleClickOutside);
            
            // Clean up SVG listeners
            const svgElements = document.querySelectorAll('svg');
            svgElements.forEach(svg => {
                svg.removeEventListener('mousedown', handleClickOutside);
                svg.removeEventListener('click', handleClickOutside);
                svg.removeEventListener('pointerup', handleClickOutside);
            });
        };
    }, [isMenuOpen]);

    useEffect(() => {
        setSectionLinkMenu(null);
    }, [skillId, isCollapsed, panelState.isEditing]);

    // Load skill description when skillId or documents actually change
    // Use JSON.stringify to detect actual changes, not just reference changes
    const documentsKey = useMemo(() => JSON.stringify(skillTreeData.documents), [skillTreeData.documents]);
    
    useEffect(() => {
        const skillIdChanged = prevSkillIdRef.current !== skillId;
        const documentsChanged = prevDocumentsRef.current !== documentsKey;
        
        // Update refs
        prevSkillIdRef.current = skillId;
        prevDocumentsRef.current = documentsKey;
        
        // Only reload if skillId changed or documents actually changed
        if (skillId) {
            if (skillIdChanged) {
                // Skill changed - reload without cache busting (normal load)
                panelInteractor.loadSkillDescription(skillId, false);
            } else if (documentsChanged) {
                // Documents changed - reload with cache busting
                panelInteractor.loadSkillDescription(skillId, true);
            } else if (!panelState.skill) {
                // Load if we don't have a skill yet
                panelInteractor.loadSkillDescription(skillId, false);
            }
        }
    }, [skillId, documentsKey, panelInteractor, panelState.skill]);

    // Reset editing state when skill changes
    useEffect(() => {
        panelInteractor.resetEditOnSkillChange(skillId);
    }, [skillId, panelInteractor]);

    // Clear the guide's DRAFT badge when a tree publish completes. Publish
    // reconciliation deletes every draft guide server-side, but the panel only
    // re-derives isGuideDraft on document loads — without this, the badge stays
    // stale until a refresh. Nullable context: tests render the panel standalone.
    const treeInteractorContext = useContext(TreeInteractorContext);
    const treeIsDraft = treeInteractorContext?.treeState.isDraft ?? false;
    const prevTreeIsDraftRef = useRef(treeIsDraft);
    useEffect(() => {
        const wasDraft = prevTreeIsDraftRef.current;
        prevTreeIsDraftRef.current = treeIsDraft;
        // Only the true→false transition means "publish just completed".
        if (wasDraft && !treeIsDraft) {
            panelInteractor.handleTreePublished();
        }
    }, [treeIsDraft, panelInteractor]);

    useEffect(() => {
        if (!skillId || !panelState.isEditing) {
            return;
        }
        const treeId = interactor.getTreeId();
        const timeoutId = window.setTimeout(() => {
            panelInteractor.saveLocalBackup(treeId, skillId, panelState.markdownContent);
        }, DRAFT_AUTOSAVE_DEBOUNCE_MS);
        return () => clearTimeout(timeoutId);
    }, [skillId, panelState.isEditing, panelState.markdownContent, interactor, panelInteractor]);

    const { onPanelTransitionEnd } = useSkillPanelFragmentScroll({
        locationHash: location.hash,
        skillId,
        skillHtml: panelState.skill?.html,
        isCollapsed,
        loading: panelState.loading,
        isEditing: panelState.isEditing,
        hasSkill: !!panelState.skill,
        scrollParentRef: panelContentRef,
        ownsDocumentScroll,
    });

    useImperativeHandle(
        ref,
        () => ({
            scrollToSection: (sectionId: string) => {
                tryScrollSkillPanelToFragment(
                    `#${sectionId}`,
                    buildSkillPanelFragmentScrollReadiness({
                        isCollapsed,
                        loading: panelState.loading,
                        hasSkill: !!panelState.skill,
                        isEditing: panelState.isEditing,
                        scrollParent: panelContentRef.current,
                    }),
                );
            },
        }),
        [isCollapsed, panelState.loading, panelState.skill, panelState.isEditing],
    );

    useEffect(() => {
        if (!isCollapsed || narrowCollapsedBounceToken < 1) {
            setNarrowBounceChrome(false);
            return;
        }
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reducedMotion) {
            onNarrowCollapsedBounceHandled?.();
            return;
        }
        const timeoutId = window.setTimeout(() => {
            setNarrowBounceChrome(true);
        }, NARROW_COLLAPSED_BOUNCE_DELAY_MS);
        return () => {
            clearTimeout(timeoutId);
            setNarrowBounceChrome(false);
        };
    }, [isCollapsed, narrowCollapsedBounceToken, onNarrowCollapsedBounceHandled]);

    const toggleCollapse = () => {
        onCollapse();
    };

    const handleCollapsedChromeClick = (e: React.MouseEvent) => {
        if (!isCollapsed || e.defaultPrevented) return;
        onCollapse();
    };

    // Get parent and child skills from interactor
    const parentSkills = panelInteractor.getParentSkills(skillId);
    const childSkills = panelInteractor.getChildSkills(skillId);
    const skillTitle = panelInteractor.getSkillTitle(skillId);

    const handleSkillGraphLinkClick = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>, nodeId: string) => {
            if (e.defaultPrevented || e.button !== 0) {
                return;
            }
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                return;
            }
            if (!onNodeSelect) {
                return;
            }
            e.preventDefault();
            onNodeSelect(nodeId);
        },
        [onNodeSelect],
    );

    const treeId = interactor.getTreeId();

    const hideCollapsedSkillPanelExpandChrome =
        !isDevelopment &&
        isCollapsed &&
        !!skillId &&
        !SkillPanelInteractor.leafHasLinkedGuide(skillTreeData, skillId);

    const closeMenu = useCallback(() => setIsMenuOpen(false), []);

    const handleEditClick = async () => {
        if (!skillId) return;
        closeMenu();
        const result = await panelInteractor.startEdit(skillId);
        if (isDevelopment && !result.ok) {
            console.warn(`SkillPanel: startEdit blocked (${result.reason})`);
        }
    };

    const handleAddGuideClick = async () => {
        if (!skillId) return;
        closeMenu();
        const result = await interactor.attachGuideToLeaf(skillId);
        if (!result.success) {
            console.error('SkillPanel: attachGuideToLeaf failed', result.error);
        }
    };

    const handleDeleteGuideMenuClick = () => {
        closeMenu();
        setShowDeleteGuideModal(true);
    };

    const handlePublishGuideClick = async () => {
        if (!skillId) return;
        closeMenu();
        const result = await panelInteractor.publishGuide(skillId);
        if (!result.ok && result.reason === 'publish_failed') {
            console.error('SkillPanel: publishGuide failed', result.error);
            window.alert(result.error);
        }
    };

    const handleDeleteGuideConfirm = async () => {
        if (!skillId) return;
        const result = await interactor.deleteGuideFromLeaf(skillId);
        if (!result.success) {
            console.error('SkillPanel: deleteGuideFromLeaf failed', result.error);
            window.alert(result.error);
            return;
        }
        setShowDeleteGuideModal(false);
    };

    const handleDeleteGuideCancel = () => {
        setShowDeleteGuideModal(false);
    };

    const handleSaveClick = async () => {
        if (!skillId) return;
        closeMenu();
        const result = await panelInteractor.save(skillId);
        if (isDevelopment && !result.ok) {
            console.warn(`SkillPanel: save did not complete (${result.reason})`);
        }
    };

    const handleCloseEdit = () => {
        closeMenu();
        panelInteractor.closeEdit();
    };

    const handleCancelEdit = () => {
        if (!skillId) return;
        if (
            !window.confirm(
                'Discard your changes? To close without discarding, use "close".',
            )
        ) {
            return;
        }
        closeMenu();
        panelInteractor.cancelEdit(skillId);
    };

    const handleCopyLink = async () => {
        if (!skillId) return;
        
        try {
            const link = panelInteractor.generateSkillLink(skillId);
            await navigator.clipboard.writeText(link);
            setIsMenuOpen(false);
        } catch (error) {
            console.error('Failed to copy link:', error);
        }
    };

    const handleCopyId = async () => {
        if (!skillId) return;
        
        try {
            await navigator.clipboard.writeText(skillId);
            setIsMenuOpen(false);
        } catch (error) {
            console.error('Failed to copy ID:', error);
        }
    };

    const handleNavigateToSection = useCallback(
        (sectionId: string) => {
            setIsMenuOpen(false);
            navigate(
                {
                    pathname: location.pathname,
                    search: location.search,
                    hash: `#${sectionId}`,
                },
                { replace: true },
            );
        },
        [navigate, location.pathname, location.search],
    );

    const handleSkillDescriptionContextMenu = useCallback(
        (e: React.MouseEvent) => {
            if (!canShareLinks) {
                return;
            }
            const headingId = resolveSectionLinkHeadingFromClickTarget(
                skillId,
                panelState.isEditing,
                e.target,
            );
            if (!headingId) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            setSectionLinkMenu({ clientX: e.clientX, clientY: e.clientY, headingId });
        },
        [skillId, panelState.isEditing, canShareLinks],
    );

    const handleDescriptionClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (
                e.defaultPrevented ||
                e.button !== 0 ||
                e.metaKey ||
                e.ctrlKey ||
                e.shiftKey ||
                e.altKey
            ) {
                return;
            }

            const target = e.target;
            if (!(target instanceof Element)) {
                return;
            }

            const action = resolveDescriptionClickAction(target, e.currentTarget, {
                currentPathname: location.pathname,
                currentOrigin: window.location.origin,
                nodes: skillTreeData.nodes,
            });

            if (action.type === 'copy_section_link') {
                if (!skillId) return;
                e.preventDefault();
                e.stopPropagation();
                const url = panelInteractor.buildSectionShareUrl(skillId, action.headingId);
                void navigator.clipboard.writeText(url).catch((err) => {
                    console.error('Failed to copy section link:', err);
                });
            } else if (action.type === 'navigate_to_node') {
                if (!onNodeSelect) return;
                e.preventDefault();
                onNodeSelect(action.nodeId);
                if (action.hash) {
                    navigate(
                        { pathname: action.pathname!, search: action.search!, hash: action.hash },
                        { replace: true },
                    );
                }
            }
        },
        [location.pathname, navigate, onNodeSelect, panelInteractor, skillId, skillTreeData.nodes],
    );

    // mdeOptions is memoized with [] so SimpleMDE is not remounted every render; refs
    // give previewRender fresh interactor/skillId without changing that stable options object.
    const panelInteractorRef = useRef(panelInteractor);
    panelInteractorRef.current = panelInteractor;
    const skillIdRef = useRef(skillId);
    skillIdRef.current = skillId;

    const mdeOptions: GuideEditorOptions = React.useMemo(() => ({
        spellChecker: false,
        status: false,
        inputStyle: 'textarea' as const,
        previewRender: (plainText: string): string => {
            const sid = skillIdRef.current;
            if (!sid) return '';
            return panelInteractorRef.current.renderPreviewHtml(plainText, sid);
        },
        toolbar: [
            'bold', 'italic', 'heading', '|',
            'quote', 'unordered-list', 'ordered-list', '|',
            'link', 'image', '|',
            'preview', 'fullscreen', '|',
            'guide'
        ] as const
    }), []);

    const canExpandFromCollapsedChrome = isCollapsed && !hideCollapsedSkillPanelExpandChrome;

    return (
        <div 
            className={`skill-panel ${isCollapsed ? 'collapsed' : ''}${narrowBounceChrome ? ' skill-panel-bounce-hint' : ''}`}
            style={{
                // Narrow viewports get exactly the screen width (the panel is anchored to
                // full-width .main-content) so every mobile device sees the same layout.
                width: isCollapsed
                    ? `${SKILL_PANEL_COLLAPSED_CHROME_WIDTH_PX}px`
                    : isNarrow
                        ? '100%'
                        : `${width}px`,
            }}
            onClick={canExpandFromCollapsedChrome ? handleCollapsedChromeClick : undefined}
            onTransitionEnd={onPanelTransitionEnd}
            onAnimationEnd={(e) => {
                if (e.animationName !== 'skill-panel-bounce-hint') return;
                if (!narrowBounceChrome) return;
                setNarrowBounceChrome(false);
                onNarrowCollapsedBounceHandled?.();
            }}
        >
            <div className={`panel-header${isCollapsed ? ' panel-header--skill-panel-collapsed' : ''}`}>
                {!isCollapsed ? (
                    <div className="skill-panel-title-row">
                        <h2>{skillTitle}</h2>
                        <div className="skill-panel-header-actions">
                            <div className="skill-panel-menu" ref={menuRef}>
                            <button
                                type="button"
                                className="skill-panel-menu-trigger"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsMenuOpen(!isMenuOpen);
                                }}
                                aria-label="Skill panel menu"
                                aria-expanded={isMenuOpen}
                            >
                                ⋯
                            </button>

                            {isMenuOpen && (
                                <div className="skill-panel-menu-dropdown">
                                    {isDevelopment && panelState.isEditing && (
                                        <>
                                            {panelState.hasLocalBackup && (
                                                <div className="compact-edit-menu-status" role="none">
                                                    Unsaved changes
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                className="skill-panel-menu-item skill-panel-menu-item-emphasis"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    void handleSaveClick();
                                                }}
                                                disabled={panelState.isSaving}
                                            >
                                                {panelState.isSaving ? 'Saving…' : 'Save'}
                                            </button>
                                            <button
                                                type="button"
                                                className="skill-panel-menu-item"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCloseEdit();
                                                }}
                                            >
                                                Close
                                            </button>
                                            <button
                                                type="button"
                                                className="skill-panel-menu-item"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCancelEdit();
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <div className="skill-panel-menu-separator" role="separator" />
                                        </>
                                    )}
                                    {shouldShowDevAddGuideMenuItem(
                                        isDevelopment,
                                        panelState,
                                        skillTreeData,
                                        skillId,
                                    ) && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="skill-panel-menu-item skill-panel-menu-item-emphasis"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAddGuideClick();
                                                    }}
                                                >
                                                    Add guide
                                                </button>
                                                <div className="skill-panel-menu-separator" role="separator" />
                                            </>
                                        )}
                                    {isDevelopment &&
                                        !panelState.isEditing &&
                                        panelState.skill &&
                                        panelState.isMarkdownLoaded && (
                                            <>
                                                {panelState.isGuideDraft && (
                                                    <div className="compact-edit-menu-status" role="none">
                                                        Draft
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    className="skill-panel-menu-item"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        void handleEditClick();
                                                    }}
                                                    disabled={panelState.isSaving}
                                                >
                                                    {panelState.isSaving ? 'Saving…' : 'Edit'}
                                                </button>
                                                {panelInteractor.canPublishGuide(skillId) && (
                                                    <button
                                                        type="button"
                                                        className="skill-panel-menu-item skill-panel-menu-item-emphasis"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            void handlePublishGuideClick();
                                                        }}
                                                        disabled={panelState.isPublishingGuide}
                                                    >
                                                        {panelState.isPublishingGuide ? 'Publishing…' : 'Publish guide'}
                                                    </button>
                                                )}
                                                {SkillPanelInteractor.leafHasLinkedGuide(skillTreeData, skillId) && (
                                                    <button
                                                        type="button"
                                                        className="skill-panel-menu-item skill-panel-menu-item-danger"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteGuideMenuClick();
                                                        }}
                                                    >
                                                        Delete guide
                                                    </button>
                                                )}
                                                <div className="skill-panel-menu-separator" role="separator" />
                                            </>
                                        )}
                                    {skillId && (
                                        <>
                                            {canShareLinks && (
                                                <button
                                                    type="button"
                                                    className="skill-panel-menu-item"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        void handleCopyLink();
                                                    }}
                                                >
                                                    Copy Link
                                                </button>
                                            )}
                                            {isDevelopment && (
                                                <button
                                                    type="button"
                                                    className="skill-panel-menu-item"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        void handleCopyId();
                                                    }}
                                                >
                                                    Copy Id
                                                </button>
                                            )}
                                        </>
                                    )}
                                    {!panelState.isEditing &&
                                        skillId &&
                                        panelState.isMarkdownLoaded &&
                                        panelState.skill &&
                                        panelState.skill.tableOfContents.length > 0 && (
                                            <div
                                                className="skill-panel-menu-toc"
                                                role="navigation"
                                                aria-label="Document sections"
                                            >
                                                <div className="skill-panel-menu-toc-label">Sections</div>
                                                <ul className="skill-panel-menu-toc-list">
                                                    {panelState.skill.tableOfContents.map((entry) => (
                                                        <li key={entry.id}>
                                                            <button
                                                                type="button"
                                                                className="skill-panel-menu-toc-item"
                                                                style={{
                                                                    paddingLeft: `calc(var(--spacing-lg) + ${(entry.depth - 1) * 12}px)`,
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleNavigateToSection(entry.id);
                                                                }}
                                                            >
                                                                {entry.title}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                </div>
                            )}
                            </div>
                            <CollapseButton direction="right" onClick={toggleCollapse} />
                        </div>
                    </div>
                ) : (
                    <div className="skill-panel-header-controls">
                        {!hideCollapsedSkillPanelExpandChrome && (
                            <CollapseButton direction="left" onClick={toggleCollapse} />
                        )}
                    </div>
                )}
            </div>
            {isCollapsed && <div className="skill-panel-collapsed-hitbox" aria-hidden="true" />}
            {!isCollapsed && (
                <div className="panel-content" ref={panelContentRef}>
                    {panelState.loading ? (
                        <p>Loading...</p>
                    ) : panelState.skill ? (
                        <>
                            {panelState.isEditing ? (
                                useTextareaEditor ? (
                                    <textarea
                                        className="skill-markdown-textarea"
                                        value={panelState.markdownContent}
                                        onChange={(e) => panelInteractor.updateMarkdownContent(e.target.value)}
                                        autoCapitalize="sentences"
                                        autoCorrect="on"
                                        spellCheck={true}
                                        aria-label="Skill guide markdown editor"
                                    />
                                ) : (
                                    <React.Suspense fallback={<p>Loading editor…</p>}>
                                        <GuideEditor
                                            value={panelState.markdownContent}
                                            onChange={(content) => panelInteractor.updateMarkdownContent(content)}
                                            options={mdeOptions}
                                        />
                                    </React.Suspense>
                                )
                            ) : (
                                <>
                                    <div
                                        className="skill-description-body"
                                        onClick={handleDescriptionClick}
                                        onContextMenu={handleSkillDescriptionContextMenu}
                                        dangerouslySetInnerHTML={{ __html: panelState.skill.html }}
                                    />

                                    {/* Parent and Child Skills Section */}
                                    <div className="skill-links-section">
                                        {/* Child Skills */}
                                        {childSkills.length > 0 && (
                                            <div style={{ marginBottom: '20px' }}>
                                                <h3 className="skill-links-header">Leads To</h3>
                                                <ol className="skill-links-list">
                                                    {childSkills.map((child) => (
                                                        <li key={child.id} className="skill-links-item">
                                                            <a
                                                                href={`${buildTreeViewUrl(treeId, child.id)}${location.search}`}
                                                                className="btn-link"
                                                                onClick={(e) => handleSkillGraphLinkClick(e, child.id)}
                                                            >
                                                                {child.name}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ol>
                                            </div>
                                        )}
                                        
                                        {/* Parent Skills */}
                                        {parentSkills.length > 0 && (
                                            <div>
                                                <h3 className="skill-links-header">Comes From</h3>
                                                <ol className="skill-links-list">
                                                    {parentSkills.map((parent) => (
                                                        <li key={parent.id} className="skill-links-item">
                                                            <a
                                                                href={`${buildTreeViewUrl(treeId, parent.id)}${location.search}`}
                                                                className="btn-link"
                                                                onClick={(e) => handleSkillGraphLinkClick(e, parent.id)}
                                                            >
                                                                {parent.name}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ol>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <p>Click any skill leaf to view its guide.</p>
                    )}
                </div>
            )}
            {sectionLinkMenu && skillId && canShareLinks && (
                <SectionLinkContextMenu
                    anchor={sectionLinkMenu}
                    shareUrl={panelInteractor.buildSectionShareUrl(skillId, sectionLinkMenu.headingId)}
                    onClose={() => setSectionLinkMenu(null)}
                />
            )}
            <ConfirmationModal
                isOpen={showDeleteGuideModal}
                title="Delete guide"
                message={`Remove the markdown guide from this skill leaf? The published site keeps its copy until you publish the tree. You can undo this with ${undoRedoModifierLabel()}+Z.`}
                confirmText="Delete guide"
                cancelText="Cancel"
                onConfirm={() => {
                    void handleDeleteGuideConfirm();
                }}
                onCancel={handleDeleteGuideCancel}
            />
        </div>
    );
};
